import React, { useRef, useEffect, useState } from 'react';
import { ReactComponent as DownIcon } from 'src/assets/u_angle-down.svg';
import { optionalCharError } from 'src/common/utils/charValidation';
import CharCount from './CharCount';

const GENRE_OPTIONS = ['시', '소설', '에세이', '인문', '자기계발', '엽서/일러스트레이션 북'];

interface ExtraFieldsSectionProps {
    desktop: boolean;
    mbti: string;
    setMbti: (v: string) => void;
    writingGenres: string[];
    setWritingGenres: (v: string[]) => void;
    inspireSource: string;
    setInspireSource: (v: string) => void;
    favoriteWorkTitle: string;
    setFavoriteWorkTitle: (v: string) => void;
    favoriteWorkReason: string;
    setFavoriteWorkReason: (v: string) => void;
    memorableEpisode: string;
    setMemorableEpisode: (v: string) => void;
    futureWritingPlan: string;
    setFutureWritingPlan: (v: string) => void;
    happiestWritingMoment: string;
    setHappiestWritingMoment: (v: string) => void;
    // Refs
    mbtiRef?: React.RefObject<HTMLInputElement>;
    inspireSourceRef?: React.RefObject<HTMLInputElement>;
    favoriteWorkTitleRef?: React.RefObject<HTMLInputElement>;
    favoriteWorkReasonRef?: React.RefObject<HTMLInputElement>;
    memorableEpisodeRef?: React.RefObject<HTMLTextAreaElement>;
    futureWritingPlanRef?: React.RefObject<HTMLInputElement>;
    happiestWritingMomentRef?: React.RefObject<HTMLInputElement>;
}

const ExtraFieldsSection: React.FC<ExtraFieldsSectionProps> = ({
    desktop,
    mbti, setMbti,
    writingGenres, setWritingGenres,
    inspireSource, setInspireSource,
    favoriteWorkTitle, setFavoriteWorkTitle,
    favoriteWorkReason, setFavoriteWorkReason,
    memorableEpisode, setMemorableEpisode,
    futureWritingPlan, setFutureWritingPlan,
    happiestWritingMoment, setHappiestWritingMoment,
    mbtiRef,
    inspireSourceRef,
    favoriteWorkTitleRef,
    favoriteWorkReasonRef,
    memorableEpisodeRef,
    futureWritingPlanRef,
    happiestWritingMomentRef,
}) => {
    const [isGenreDropdownOpen, setIsGenreDropdownOpen] = useState(false);
    const genreDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (genreDropdownRef.current && !genreDropdownRef.current.contains(event.target as Node)) {
                setIsGenreDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleGenre = (g: string) => {
        if (writingGenres.includes(g)) {
            setWritingGenres(writingGenres.filter(x => x !== g));
        } else if (writingGenres.length < 2) {
            setWritingGenres([...writingGenres, g]);
        } else {
            alert('최대 2개까지 선택 가능합니다.');
        }
    };

    // 공용 스타일
    const inputCls = `appearance-none h-12 w-full font-namsan border border-extra placeholder-[#5E676E] focus:outline-input rounded py-3 px-4 leading-tight focus:text-black`;
    const textareaCls = `appearance-none w-full font-namsan border border-extra placeholder-[#5E676E] focus:outline-input rounded py-3 px-4 leading-tight focus:text-black`;
    const wrapCls = desktop ? 'flex flex-col gap-8 mt-10' : 'flex flex-col gap-6';
    const titleCls = desktop ? 'title' : 'text-base font-bold text-black';
    const subTitleCls = desktop ? 'subTitle' : 'text-sm font-semibold text-black mt-1';
    const inputStyle = {};
    const textareaStyle = { height: '160px' };
    const genreStyle: React.CSSProperties = desktop ? { width: '400px' } : { width: '100%' };


    return (
        <div className={wrapCls}>
            <p className={desktop ? 'text-[20px] font-bold text-main mt-6' : 'text-[18px] font-bold text-main'}>
                작가님에 대해 조금 더 알려주세요 😊
            </p>

            {/* MBTI */}
            <div className={`flex flex-col ${desktop ? "gap-3" : "gap-2"}`}>
                <span className={titleCls}>나의 MBTI</span>
                <input
                    ref={mbtiRef}
                    className={inputCls}
                    style={desktop ? { width: '200px' } : { width: '100%' }}
                    type="text"
                    value={mbti}
                    maxLength={10}
                    placeholder="예) INFJ"
                    onChange={(e) => setMbti(e.target.value)}
                />
            </div>

            {/* 자주 쓰는 글 장르 – AddBook과 동일한 드롭다운 UI */}
            <div className={`flex flex-col ${desktop ? "gap-3" : "gap-2"}`}>
                <span className={titleCls}>
                    자주 쓰는 글 장르{' '}
                    <span className="text-gray-400 text-sm font-normal">(최대 2개)</span>
                </span>
                <div className="relative" ref={genreDropdownRef} style={genreStyle}>
                    <button
                        type="button"
                        onClick={() => setIsGenreDropdownOpen(!isGenreDropdownOpen)}
                        className="w-full p-3 border border-[#19CF74] rounded-2xl text-sm bg-white flex items-center justify-between text-gray-700"
                    >
                        <span>
                            {writingGenres.length > 0 ? writingGenres.join(', ') : '장르를 선택하세요'}
                        </span>
                        <DownIcon />
                    </button>
                    {isGenreDropdownOpen && (
                        <div className="absolute w-full bg-white border border-gray-300 rounded-lg shadow-md z-20 mt-1">
                            {GENRE_OPTIONS.map((g) => (
                                <div
                                    key={g}
                                    className="flex items-center p-2 hover:bg-[#ADF3D340] cursor-pointer"
                                    onClick={() => toggleGenre(g)}
                                >
                                    <input
                                        type="checkbox"
                                        readOnly
                                        checked={writingGenres.includes(g)}
                                        className="w-5 h-5 mr-3 accent-[#19CF74] pointer-events-none"
                                    />
                                    <label className="text-gray-800 text-sm cursor-pointer">{g}</label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* 영감을 받는 곳 */}
            <div className={`flex flex-col ${desktop ? "gap-3" : "gap-2"}`}>
                <span className={titleCls}>글을 쓸 때 주로 어디에서 영감을 받으시는 편이신가요?</span>
                <div className="flex flex-col">
                    <input
                        ref={inspireSourceRef}
                        className={inputCls}
                        style={inputStyle}
                        type="text"
                        value={inspireSource}
                        maxLength={1000}
                        placeholder="영감을 받는 곳을 자유롭게 적어주세요"
                        onChange={(e) => setInspireSource(e.target.value)}
                    />
                    <CharCount current={inspireSource.length} max={1000} error={optionalCharError(inspireSource)} style={inputStyle} />
                </div>
            </div>

            {/* 출간작 중 가장 마음에 드는 작품 */}
            <div className={`flex flex-col ${desktop ? "gap-3" : "gap-2"}`}>
                <span className={titleCls}>출간작 중 가장 마음에 드는 작품과 그 이유는 무엇인가요?</span>
                <div className="flex flex-col gap-2">
                    <p className={subTitleCls}>작품 제목</p>
                    <input
                        ref={favoriteWorkTitleRef}
                        className={inputCls}
                        style={inputStyle}
                        type="text"
                        value={favoriteWorkTitle}
                        maxLength={100}
                        placeholder="작품 제목을 입력해 주세요"
                        onChange={(e) => setFavoriteWorkTitle(e.target.value)}
                    />
                </div>
                <div className="flex flex-col gap-2">
                    <p className={subTitleCls}>이유</p>
                    <input
                        ref={favoriteWorkReasonRef}
                        className={inputCls}
                        style={inputStyle}
                        type="text"
                        value={favoriteWorkReason}
                        maxLength={1000}
                        placeholder="그 작품을 선택하신 이유를 적어주세요"
                        onChange={(e) => setFavoriteWorkReason(e.target.value)}
                    />
                    <CharCount current={favoriteWorkReason.length} max={1000} error={optionalCharError(favoriteWorkReason)} style={inputStyle} />
                </div>
            </div>

            {/* 에피소드 */}
            <div className={`flex flex-col ${desktop ? "gap-3" : "gap-2"}`}>
                <span className={titleCls}>작가로 활동하면서 잊을 수 없었던 에피소드 한 가지!</span>
                <div className="flex flex-col">
                    <textarea
                        ref={memorableEpisodeRef}
                        className={textareaCls}
                        style={textareaStyle}
                        value={memorableEpisode}
                        maxLength={1000}
                        placeholder="잊을 수 없는 에피소드를 자유롭게 적어주세요"
                        onChange={(e) => setMemorableEpisode(e.target.value)}
                    />
                    <CharCount current={memorableEpisode.length} max={1000} error={optionalCharError(memorableEpisode)} style={textareaStyle} />
                </div>
            </div>

            {/* 앞으로 계획 */}
            <div className={`flex flex-col ${desktop ? "gap-3" : "gap-2"}`}>
                <span className={titleCls}>앞으로 어떤 글을 더 써보실 계획이신가요?</span>
                <div className="flex flex-col">
                    <input
                        ref={futureWritingPlanRef}
                        className={inputCls}
                        style={inputStyle}
                        type="text"
                        value={futureWritingPlan}
                        maxLength={1000}
                        placeholder="앞으로 써보고 싶은 글을 자유롭게 적어주세요"
                        onChange={(e) => setFutureWritingPlan(e.target.value)}
                    />
                    <CharCount current={futureWritingPlan.length} max={1000} error={optionalCharError(futureWritingPlan)} style={inputStyle} />
                </div>
            </div>

            {/* 가장 행복한 순간 */}
            <div className={`flex flex-col ${desktop ? "gap-3" : "gap-2"}`}>
                <span className={titleCls}>작가로서 가장 행복한 순간은 언제인가요?</span>
                <div className="flex flex-col">
                    <input
                        ref={happiestWritingMomentRef}
                        className={inputCls}
                        style={inputStyle}
                        type="text"
                        value={happiestWritingMoment}
                        maxLength={1000}
                        placeholder="작가 활동 중 가장 보람찼던 순간을 적어주세요"
                        onChange={(e) => setHappiestWritingMoment(e.target.value)}
                    />
                    <CharCount current={happiestWritingMoment.length} max={1000} error={optionalCharError(happiestWritingMoment)} style={inputStyle} />
                </div>
            </div>
        </div>
    );
};

export default ExtraFieldsSection;
